import {
    ArrowBack as ArrowBackIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

const CourseSubject = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const apiURL = process.env.REACT_APP_API;

    const courseid = searchParams.get('courseid');
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingSubjectId, setEditingSubjectId] = useState('');

    // Delete confirmation dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [subjectToDelete, setSubjectToDelete] = useState(null);

    // Validation schema
    const validationSchema = Yup.object({
        s_subject: Yup.string()
            .required('Subject name is required')
            .min(2, 'Subject name must be at least 2 characters'),
        s_fmark: Yup.number()
            .required('Full marks is required')
            .positive('Full marks must be positive')
            .integer('Full marks must be an integer'),
        s_pmark: Yup.number()
            .required('Pass marks is required')
            .positive('Pass marks must be positive')
            .integer('Pass marks must be an integer')
            .test('less-than-full', 'Pass marks must be less than or equal to full marks', function (value) {
                return value <= this.parent.s_fmark;
            })
    });

    // Formik setup
    const formik = useFormik({
        initialValues: {
            s_subject: '',
            s_fmark: '',
            s_pmark: ''
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            if (!courseid) {
                toast.error('Course ID is required');
                return;
            }

            setSubmitting(true);
            try {
                const endpoint = editingSubjectId
                    ? `${apiURL}/admin/course/subject/update`
                    : `${apiURL}/admin/course/subject/add`;

                const payload = editingSubjectId
                    ? { subjectid: editingSubjectId, courseid, ...values }
                    : { courseid, ...values };

                const response = await axios.post(endpoint, payload, {
                    headers: { 'Content-Type': 'application/json' }
                });

                if (response.data.status === 'success' || response.data.status === true) {
                    const message = editingSubjectId ? 'Subject updated successfully' : 'Subject added successfully';
                    toast.success(message);
                    enqueueSnackbar(message, { variant: 'success' });

                    // Reset form and fetch updated list
                    formik.resetForm();
                    setEditingSubjectId('');
                    fetchSubjects();
                } else {
                    toast.error(response.data.message || 'Operation failed');
                    enqueueSnackbar(response.data.message || 'Operation failed', { variant: 'error' });
                }
            } catch (error) {
                console.error('Error saving subject:', error);
                toast.error(error.response?.data?.message || 'Failed to save subject');
                enqueueSnackbar('Failed to save subject', { variant: 'error' });
            } finally {
                setSubmitting(false);
            }
        }
    });

    // Fetch subjects list
    const fetchSubjects = async () => {
        if (!courseid) {
            toast.error('Course ID is required');
            navigate(-1);
            return;
        }

        setLoading(true);
        try {
            const response = await axios.get(`${apiURL}/admin/course/subject/getall?courseid=${courseid}`);

            if (response.data.status === 'success' || response.data.status === true) {
                setSubjects(response.data.data || []);
            } else {
                setSubjects([]);
                if (response.data.message) {
                    toast.info(response.data.message);
                }
            }
        } catch (error) {
            console.error('Error fetching subjects:', error);
            toast.error(error.response?.data?.message || 'Failed to fetch subjects');
            setSubjects([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch subject details for editing
    const handleEdit = async (subjectid) => {
        try {
            const response = await axios.get(
                `${apiURL}/admin/course/subject/details?courseid=${courseid}&subjectid=${subjectid}`
            );

            if (response.data.status === 'success' || response.data.status === true) {
                const data = response.data.data;
                formik.setValues({
                    s_subject: data.s_subject || '',
                    s_fmark: data.s_fmark || '',
                    s_pmark: data.s_pmark || ''
                });
                setEditingSubjectId(subjectid);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                toast.error(response.data.message || 'Failed to fetch subject details');
            }
        } catch (error) {
            console.error('Error fetching subject details:', error);
            toast.error(error.response?.data?.message || 'Failed to fetch subject details');
        }
    };

    // Open delete confirmation dialog
    const handleDeleteClick = (subject) => {
        setSubjectToDelete(subject);
        setDeleteDialogOpen(true);
    };

    // Close delete confirmation dialog
    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setSubjectToDelete(null);
    };

    // Confirm and delete subject
    const handleDeleteConfirm = async () => {
        if (!subjectToDelete) return;

        try {
            const response = await axios.post(
                `${apiURL}/admin/course/subject/delete`,
                { subjectid: subjectToDelete.subjectid, courseid },
                { headers: { 'Content-Type': 'application/json' } }
            );

            if (response.data.status === 'success' || response.data.status === true) {
                toast.success('Subject deleted successfully');
                enqueueSnackbar('Subject deleted successfully', { variant: 'success' });
                fetchSubjects();

                // Clear form if deleting the subject being edited
                if (editingSubjectId === subjectToDelete.subjectid) {
                    formik.resetForm();
                    setEditingSubjectId('');
                }
            } else {
                toast.error(response.data.message || 'Failed to delete subject');
            }
        } catch (error) {
            console.error('Error deleting subject:', error);
            toast.error(error.response?.data?.message || 'Failed to delete subject');
        } finally {
            handleDeleteCancel();
        }
    };

    // Cancel edit
    const handleCancelEdit = () => {
        formik.resetForm();
        setEditingSubjectId('');
    };

    useEffect(() => {
        fetchSubjects();
    }, [courseid]);

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <Box sx={{ py: 4 }} className="content">
            <Container maxWidth="lg">
                {/* Header */}
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        variant="outlined"
                    >
                        Back
                    </Button>
                    <Typography variant="h4" fontWeight={600}>
                        Course Subjects
                    </Typography>
                </Stack>

                {/* Add/Edit Form */}
                <Card sx={{ mb: 3, borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            {editingSubjectId ? 'Edit Subject' : 'Add New Subject'}
                        </Typography>
                        <form onSubmit={formik.handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id="s_subject"
                                        name="s_subject"
                                        label="Subject Name"
                                        value={formik.values.s_subject}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.s_subject && Boolean(formik.errors.s_subject)}
                                        helperText={formik.touched.s_subject && formik.errors.s_subject}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        id="s_fmark"
                                        name="s_fmark"
                                        label="Full Marks"
                                        type="number"
                                        value={formik.values.s_fmark}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.s_fmark && Boolean(formik.errors.s_fmark)}
                                        helperText={formik.touched.s_fmark && formik.errors.s_fmark}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        id="s_pmark"
                                        name="s_pmark"
                                        label="Pass Marks"
                                        type="number"
                                        value={formik.values.s_pmark}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.s_pmark && Boolean(formik.errors.s_pmark)}
                                        helperText={formik.touched.s_pmark && formik.errors.s_pmark}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                                        {editingSubjectId && (
                                            <Button
                                                variant="outlined"
                                                onClick={handleCancelEdit}
                                                disabled={submitting}
                                            >
                                                Cancel
                                            </Button>
                                        )}
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            startIcon={<SaveIcon />}
                                            disabled={submitting}
                                        >
                                            {submitting ? (
                                                <CircularProgress size={24} color="inherit" />
                                            ) : (
                                                editingSubjectId ? 'Update Subject' : 'Add Subject'
                                            )}
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </form>
                    </CardContent>
                </Card>

                {/* Subjects Table */}
                <Card sx={{ borderRadius: 2 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                            <CircularProgress size={60} />
                        </Box>
                    ) : subjects.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Alert severity="info">No subjects found. Add your first subject above.</Alert>
                        </Box>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead sx={{ bgcolor: 'action.hover' }}>
                                    <TableRow>
                                        <TableCell><strong>#</strong></TableCell>
                                        <TableCell><strong>Subject</strong></TableCell>
                                        <TableCell align="right"><strong>Full Marks</strong></TableCell>
                                        <TableCell align="right"><strong>Pass Marks</strong></TableCell>
                                        <TableCell align="center"><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {subjects.map((subject, index) => (
                                        <TableRow
                                            key={subject.subjectid}
                                            sx={{
                                                bgcolor: editingSubjectId === subject.subjectid ? 'action.selected' : 'inherit',
                                                '&:hover': { bgcolor: 'action.hover' }
                                            }}
                                        >
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{subject.s_subject}</TableCell>
                                            <TableCell align="right">{subject.s_fmark}</TableCell>
                                            <TableCell align="right">{subject.s_pmark}</TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={1} justifyContent="center">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleEdit(subject.subjectid)}
                                                        title="Edit"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteClick(subject)}
                                                        title="Delete"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Card>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={handleDeleteCancel}
                    aria-labelledby="delete-dialog-title"
                    aria-describedby="delete-dialog-description"
                >
                    <DialogTitle id="delete-dialog-title">
                        Confirm Delete
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="delete-dialog-description">
                            Are you sure you want to delete the subject <strong>"{subjectToDelete?.s_subject}"</strong>?
                            <br />
                            This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteCancel} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default CourseSubject;